/**
 * useBurnProgress — Current month burn progress from bank transactions
 *
 * Real-time onSnapshot listener for ART_INCOME transactions in current month.
 * Burn card updates instantly when transactions are categorized.
 */

import { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/config';
import { useAuth } from '@/contexts/AuthContext';

interface BurnProgress {
  earnedCents: number;
  burnTargetCents: number;
  progressPct: number;
  dayFraction: number;
  onTrack: boolean;
  isLoading: boolean;
  lifeBurnCents: number;
  studioBurnCents: number;
  hasBurnSplit: boolean;
  dailyCumulativeIncome: number[];
  /** Day-of-month numbers that had ART_INCOME deposits */
  incomeDays: number[];
  /** Per-category totals from unlimited current-month query */
  materialsCents: number;
  overheadCents: number;
  personalCents: number;
}

export function useBurnProgress(): BurnProgress {
  const { user, profile } = useAuth();
  const [earnedCents, setEarnedCents] = useState(0);
  const [dailyCumulativeIncome, setDailyCumulativeIncome] = useState<number[]>([]);
  const [incomeDays, setIncomeDays] = useState<number[]>([]);
  const [lifeBurnCents, setLifeBurnCents] = useState(0);
  const [studioBurnCents, setStudioBurnCents] = useState(0);
  const [materialsCents, setMaterialsCents] = useState(0);
  const [overheadCents, setOverheadCents] = useState(0);
  const [personalCents, setPersonalCents] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const burnTargetCents = profile?.monthlyBurn ?? 0;

  // Current month start date string "YYYY-MM-01"
  const monthStart = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}-01`;
  }, []);

  // Day fraction: how far through the month we are
  const dayFraction = useMemo(() => {
    const now = new Date();
    const day = now.getDate();
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return day / daysInMonth;
  }, []);

  useEffect(() => {
    if (!user || !profile) {
      setIsLoading(false);
      return;
    }

    const txnRef = collection(db, 'users', user.uid, 'transactions');
    const q = query(
      txnRef,
      where('tpCategory', '==', 'ART_INCOME'),
      where('date', '>=', monthStart),
      where('pending', '==', false)
    );

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const now = new Date();
        const dayOfMonth = now.getDate();

        // Bucket income by day-of-month
        const dailyBuckets = new Array(dayOfMonth).fill(0);
        let total = 0;
        snap.forEach((doc) => {
          const data = doc.data();
          const amt = Math.abs(data.amount || 0);
          total += amt;
          // Parse day from date string "YYYY-MM-DD"
          const dayStr = (data.date as string)?.slice(8, 10);
          const day = parseInt(dayStr, 10);
          if (day >= 1 && day <= dayOfMonth) {
            dailyBuckets[day - 1] += amt;
          }
        });

        // Convert to cumulative
        const cumulative: number[] = [];
        let running = 0;
        for (let i = 0; i < dayOfMonth; i++) {
          running += dailyBuckets[i];
          cumulative.push(running);
        }

        // Derive which days had income
        const days: number[] = [];
        for (let i = 0; i < dayOfMonth; i++) {
          if (dailyBuckets[i] > 0) days.push(i + 1);
        }

        setEarnedCents(total);
        setDailyCumulativeIncome(cumulative);
        setIncomeDays(days);
        setIsLoading(false);
      },
      (err) => {
        console.error('[useBurnProgress] onSnapshot error:', err);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user, profile, monthStart]);

  // Expense split: life vs studio burn
  useEffect(() => {
    if (!user || !profile) {
      setLifeBurnCents(0);
      setStudioBurnCents(0);
      return;
    }

    const txnRef = collection(db, 'users', user.uid, 'transactions');
    const q2 = query(
      txnRef,
      where('pending', '==', false),
      where('date', '>=', monthStart),
      where('tpCategory', 'in', ['PERSONAL', 'BUSINESS_EXPENSE', 'ART_SUPPLY'])
    );

    const unsubscribe = onSnapshot(
      q2,
      (snap) => {
        let life = 0;
        let studio = 0;
        let materials = 0;
        let overhead = 0;
        let personal = 0;
        snap.forEach((doc) => {
          const data = doc.data();
          const amt = Math.abs(data.amount || 0);
          if (data.tpCategory === 'PERSONAL') {
            life += amt;
            personal += amt;
          } else if (data.tpCategory === 'ART_SUPPLY') {
            studio += amt;
            materials += amt;
          } else if (data.tpCategory === 'BUSINESS_EXPENSE') {
            studio += amt;
            overhead += amt;
          }
        });
        setLifeBurnCents(life);
        setStudioBurnCents(studio);
        setMaterialsCents(materials);
        setOverheadCents(overhead);
        setPersonalCents(personal);
      },
      (err) => {
        console.error('[useBurnProgress] expense split error:', err);
      }
    );

    return () => unsubscribe();
  }, [user, profile, monthStart]);

  const progressPct = burnTargetCents > 0 ? Math.min(100, (earnedCents / burnTargetCents) * 100) : 0;
  const onTrack = progressPct >= dayFraction * 100;
  const hasBurnSplit = studioBurnCents > 0;

  return {
    earnedCents,
    burnTargetCents,
    progressPct,
    dayFraction,
    onTrack,
    isLoading,
    lifeBurnCents,
    studioBurnCents,
    hasBurnSplit,
    dailyCumulativeIncome,
    incomeDays,
    materialsCents,
    overheadCents,
    personalCents,
  };
}

export default useBurnProgress;
